<template>
  <ConditionalColorPicker
    id="font-color-picker"
    label="Font Color"
    type="color"
    class="tw-mb-4"
    :initialColor="desktopColor.style"
    @colorChange="onFontColorChange"
  />
  <FontDropdown
    :selectedFontOption="selectedFontOption"
    :moduleId="moduleId"
    :hideFontDropdown="hideFontDropdown"
    class="tw-mb-4"
    @setSelectedFontOption="setSelectedFontOption"
  />

  <div v-show="selectedFontOption === 'Web Font'" class="tw-mb-4">
    <StudioInput
      id="web-font-name-input"
      ref="webFontNameInput"
      :modelValue="custom_font_name"
      required
      label="Font Name"
      class="tw-mb-4"
      :inlineError="fontNameInvalidError"
      @update:modelValue="handleUpdateFontName"
    />

    <StudioInput
      id="web-font-url-input"
      ref="webFontUrlInput"
      :modelValue="theme_font_link"
      required
      label="Font URL"
      placeholder="https://..."
      :inlineError="fontUrlInvalidError"
      @update:modelValue="handleUpdateFontURL"
    />
  </div>
  <div v-show="selectedFontOption === 'Custom'" class="tw-mb-4">
    <div class="tw-flex tw-items-center tw-justify-between tw-gap-2">
      <a
        v-if="themeFontSrc"
        :href="themeFontSrc"
        target="_blank"
        class="tw-w-[300px] tw-overflow-hidden tw-overflow-ellipsis tw-text-sm"
      >
        {{ customFontName }}
      </a>
      <FilestackS3
        :s3path="s3BucketPath"
        :acceptedTypes="FONT_FILE_TYPES"
        :security="filestackSecurity"
        @fileuploaded="onFileUpload"
      >
        <StudioButton trackingId="upload-font-file-button" :variant="themeFontSrc ? 'outline' : 'warn'">
          {{ themeFontSrc ? 'Change File' : 'Upload File' }}
        </StudioButton>
      </FilestackS3>
    </div>
    <div class="tw-mt-2 tw-text-xs tw-text-slate-500">Supported Formats - TTF, OTF, WOFF, WOFF2</div>
  </div>
</template>

<script>
import logger from '@goldcast/logger';
import throttle from 'lodash.throttle';
import { mapState, mapMutations, mapGetters } from 'vuex';

import FontDropdown from './FontDropdown.vue';
import { color } from '../../data/defaultValues';

import { getFilestackSecurity } from '@/components/core/Filestack/filestackSecurity';
import { GET_RESOURCES_PATH, STATIC_ASSETS, URL_PATTERN, FONT_FILE_TYPES } from '@/constants';
import ConditionalColorPicker from '@/studio/lookAndFeel/components/ConditionalColorPicker.vue';
import StudioButton from '@/studioComponents/StudioButton.vue';
import StudioInput from '@/studioComponents/StudioInput.vue';
import { destructureRgbColor } from '@/utils/color.ts';
import { showErrorNotification } from '@/utils/utils.js';

export default {
  name: 'CustomFont',
  components: {
    FontDropdown,
    StudioInput,
    StudioButton,
    ConditionalColorPicker
  },
  props: {
    moduleId: {
      type: String,
      required: true
    }
  },
  data() {
    return {
      s3BucketPath: `${GET_RESOURCES_PATH(this.$route.params.eventId)}`,
      desktopColor: color,
      selectedFontOption: 'Inter',
      custom_font_name: '',
      theme_font_link: '',
      hideFontDropdown: true,
      customThemeFontFilename: '',
      fontNameInvalidError: '',
      fontUrlInvalidError: '',
      FONT_FILE_TYPES,
      filestackSecurity: null
    };
  },
  async mounted() {
    this.initializeData();
    if (this.$store.getters['org/isSignedFilestackUploadsEnabled']) {
      try {
        const { security } = await getFilestackSecurity({
          intent: 'event_asset',
          assetKind: 'font',
          event: this.$route.params.eventId
        });
        this.filestackSecurity = security;
      } catch (e) {
        logger.warn('Failed to fetch Filestack security, upload will fail', e);
      }
    }
  },
  computed: {
    ...mapState('branding', ['isCurrentModuleInValid', 'changesMadeInThemeObj']),
    ...mapGetters('branding', {
      getInitialModuleData: 'getInitialModuleData'
    }),
    themeType() {
      const moduleKeys = ['theme_type'];
      const moduleData = this.getInitialModuleData(moduleKeys);
      return moduleData.theme_type;
    },
    themeFontSrc() {
      const moduleKeys = ['theme_font_src', 'splash_theme_font_src'];
      const moduleData = this.getInitialModuleData(moduleKeys);

      return moduleData.splash_theme_font_src;
    },
    customFontName() {
      if (!this.themeFontSrc) {
        return '';
      }

      if (this.customThemeFontFilename) {
        return this.customThemeFontFilename;
      }

      const url = decodeURIComponent(this.themeFontSrc);
      const rawFileName = url.split('/').pop();
      const prefix = rawFileName.split('_')[0];
      const fileName = rawFileName.split(prefix + '_')[1];
      return fileName || 'Custom Font';
    },
    isFormValid() {
      if (this.hideFontDropdown) {
        return true;
      }

      if (this.selectedFontOption === 'Web Font') {
        return !this.fontNameInvalidError && !this.fontUrlInvalidError;
      }
      if (this.selectedFontOption === 'Custom') {
        return this.themeFontSrc;
      }
      return true;
    }
  },
  watch: {
    isFormValid(isValid) {
      this.setCurrentModuleInvalid(!isValid);
    },
    themeType() {
      this.setSelectedFontOption('Inter');
    }
  },
  methods: {
    ...mapMutations('branding', {
      setChangesMadeInThemeObj: 'setChangesMadeInThemeObj',
      setCurrentModuleInvalid: 'setCurrentModuleInvalid'
    }),
    initializeData() {
      const moduleKeys = [
        'primary_text_color',
        'theme_font_name',
        'theme_font_link',
        'font_type',
        'custom_font_name',
        'splash_text_color',
        'splash_theme_font_name',
        'splash_theme_font_link',
        'splash_font_type',
        'splash_custom_font_name',
        'splash_theme_font_src',
        'theme_font_src',
        'splash_text_onimage_color'
      ];
      const {
        primary_text_color,
        custom_font_name,
        theme_font_link,
        font_type,
        theme_font_name,
        splash_text_color,
        splash_theme_font_name,
        splash_theme_font_link,
        splash_font_type,
        splash_custom_font_name,
        splash_theme_font_src,
        theme_font_src,
        splash_text_onimage_color
      } = this.getInitialModuleData(moduleKeys);

      // check if event landing and stage fonts are same
      // for doing that check if any of the below conditions are true (using or statements)
      // splash_text_color equals primary_text_color
      // custom_font_name equals splash_custom_font_name
      // theme_font_link equals splash_theme_font_link
      // font_type is Custom, splash_font_type is custom and theme_font_src equals splash_theme_font_src
      // theme_font_name == splash_theme_font_name
      const isEventAndStageFontSame =
        (primary_text_color === splash_text_color || splash_text_color === 'var(--primary-text-color)') &&
        custom_font_name === splash_custom_font_name &&
        theme_font_link === splash_theme_font_link &&
        (font_type === splash_font_type ||
          (font_type === 'Custom' &&
            splash_font_type === 'Custom Splash' &&
            splash_theme_font_src === theme_font_src)) &&
        theme_font_name === splash_theme_font_name;

      if (isEventAndStageFontSame) {
        this.hideFontDropdown = true;
      } else {
        this.hideFontDropdown = false;
      }

      this.desktopColor = destructureRgbColor(splash_text_onimage_color) || color;
      this.custom_font_name = splash_custom_font_name;
      this.theme_font_link = splash_theme_font_link;
      if (splash_font_type === 'Default' || splash_font_type === 'Inter' || splash_font_type === 'Web Safe Font') {
        this.selectedFontOption =
          splash_theme_font_name || (splash_font_type === 'Default' ? 'Default-GT-America' : 'Inter');
      } else {
        this.selectedFontOption = splash_font_type === 'Custom Splash' ? 'Custom' : splash_font_type;
      }
    },
    handleUpdateFontName(value) {
      this.custom_font_name = value;
      this.validateFontName();
    },
    handleUpdateFontURL(value) {
      this.theme_font_link = value;
      this.validateFontUrl();
    },
    onFontColorChange(color) {
      this.desktopColor = color;
      this.setChangesMadeInThemeObj({
        splash_text_onimage_color: color.style
      });
    },
    validateFontName: throttle(function () {
      if (!this.custom_font_name?.trim()) {
        this.fontNameInvalidError = 'Font name is required';
      } else {
        this.makeChangesInThemeObj();
        this.fontNameInvalidError = '';
      }
    }, 200),
    validateFontUrl: throttle(function () {
      if (!this.theme_font_link?.trim()) {
        this.fontUrlInvalidError = 'Font URL is required';
      } else if (!URL_PATTERN.test(this.theme_font_link)) {
        this.fontUrlInvalidError = 'Invalid URL';
      } else {
        this.fontUrlInvalidError = '';
        this.makeChangesInThemeObj();
      }
    }, 200),
    makeChangesInThemeObj() {
      if (this.hideFontDropdown === true) {
        this.setChangesMadeInThemeObj({
          splash_custom_font_name: this.custom_font_name,
          custom_font_name: this.custom_font_name,
          theme_font_link: this.theme_font_link,
          splash_theme_font_link: this.theme_font_link
        });
      } else {
        this.setChangesMadeInThemeObj({
          splash_custom_font_name: this.custom_font_name,
          splash_theme_font_link: this.theme_font_link
        });
      }
    },
    onFileUpload(files) {
      const { filesUploaded, filesFailed } = files;

      if (filesFailed?.length !== 0 || !filesUploaded?.length) {
        showErrorNotification('File Upload Failed');
        return;
      }

      const uploadedFileName = filesUploaded[0]?.originalFile?.name;
      const resourceKey = encodeURIComponent(filesUploaded[0]?.key || '');
      const url = `${STATIC_ASSETS.CLOUDFRONT_URL}/${resourceKey}`;
      this.customThemeFontFilename = uploadedFileName;

      if (this.hideFontDropdown === true) {
        this.setChangesMadeInThemeObj({
          theme_font_src: url,
          splash_theme_font_src: url
        });
      } else {
        this.setChangesMadeInThemeObj({
          splash_theme_font_src: url
        });
      }
    },
    setSelectedFontOption(selectedFontOption) {
      this.selectedFontOption = selectedFontOption;

      if (selectedFontOption === 'Web Font') {
        this.validateFontName();
        this.validateFontUrl();
      }
    }
  }
};
</script>
